/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Permission } from '@/config/permissions';
import type { RoleCode } from '@/config/role-codes';
import type { IUserProfileRole } from '@/features/UserManagement/services/userApis';
import { useGetUserProfileQuery } from '@/features/UserManagement/services/usersApi';
import type { AccessSnapshot } from '@/types/access';
import type { UserRole } from '@/types/role';
import {
  createAccessSnapshot,
  EMPTY_ACCESS_SNAPSHOT,
  getActiveUserRole,
  getPrimaryRoleCode,
  hasAllPermissions,
  hasAnyPermission,
  hasAnyRoleCode,
  hasModuleAccess,
  hasPermission,
  hasRoleCode,
  hasUserRole,
} from '@/utils/accessControl';

type AccessContextValue = {
  profile: ReturnType<typeof useGetUserProfileQuery>['data'];
  snapshot: AccessSnapshot;
  role: UserRole;
  userRoles: IUserProfileRole[];
  /** Preferred role code from profile (undefined on first login). */
  preferredRoleCode: string | undefined;
  /** Role shown in the sidebar dropdown (pending selection or backend preferred). */
  activeRoleCode: string | undefined;
  selectRole: (roleCode: RoleCode) => void;
  /** @deprecated Dev-only role override. */
  setRole: (next: UserRole) => void;
  isLoading: boolean;
  /** True whenever a profile request is in flight, including a role switch — unlike `isLoading`,
   * which only covers the very first load (RTK Query keeps returning the previous role's `data`,
   * so `isLoading` reads false, while the new role's request is still in flight). */
  isFetching: boolean;
  isDevRoleOverride: boolean;
  can: (permission: Permission | string) => boolean;
  canAny: (permissions: readonly (Permission | string)[]) => boolean;
  canAll: (permissions: readonly (Permission | string)[]) => boolean;
  hasRole: (roleCode: RoleCode | string) => boolean;
  hasAnyRole: (roleCodes: readonly (RoleCode | string)[]) => boolean;
  hasAppRole: (role: UserRole) => boolean;
  hasModule: (moduleCode: string) => boolean;
};

const AccessContext = createContext<AccessContextValue | null>(null);

type AccessProviderProps = {
  children: ReactNode;
};

export const AccessProvider = ({ children }: AccessProviderProps) => {
  const [roleCodeParam, setRoleCodeParam] = useState<string | undefined>(undefined);
  const [devRoleOverride, setDevRoleOverride] = useState<UserRole | null>(null);

  const profileQueryArg = useMemo(
    () => (roleCodeParam ? { roleCode: roleCodeParam } : undefined),
    [roleCodeParam]
  );

  // `refetchOnMountOrArgChange`: selecting a role is a request with side effects (the server
  // stores the role), so switching back to a role used before must call the API again instead of
  // being served from the cache by its `{ roleCode }` argument.
  const {
    data: profile,
    isLoading,
    isFetching,
  } = useGetUserProfileQuery(profileQueryArg, { refetchOnMountOrArgChange: true });
  const userRoles = useMemo(() => profile?.roles ?? [], [profile?.roles]);
  const preferredRoleCode = profile?.preferredRole?.code;
  const activeRoleCode =
    roleCodeParam ?? preferredRoleCode ?? getPrimaryRoleCode(userRoles) ?? undefined;

  const snapshot = useMemo(() => {
    if (!profile) return EMPTY_ACCESS_SNAPSHOT;
    return createAccessSnapshot({
      roles: profile.roles,
      permissions: profile.permissions,
      modules: profile.modules,
    });
  }, [profile]);

  const apiRole = useMemo(
    () => (profile ? getActiveUserRole(profile.roles, profile.preferredRole?.code) : 'Employee'),
    [profile]
  );

  const isDevRoleOverride = import.meta.env.DEV && devRoleOverride !== null;
  const role = isDevRoleOverride ? devRoleOverride! : apiRole;

  // From the moment a role is selected until its profile request has finished. Without it there is
  // a render in between (a previously used role's cached profile is already "the answer") in which
  // role-dependent screens mount and fire requests while the server still has the old role.
  const [pendingRole, setPendingRole] = useState<string | undefined>(undefined);
  const hasSeenSwitchFetch = useRef(false);

  const selectRole = useCallback(
    (roleCode: RoleCode) => {
      // Same argument as the one in use: nothing will be requested, so there is nothing to wait for.
      if (roleCode === roleCodeParam) return;
      // A new selection starts a new wait: the fetch seen for an earlier one doesn't count.
      hasSeenSwitchFetch.current = false;
      setRoleCodeParam(roleCode);
      setPendingRole(roleCode);
    },
    [roleCodeParam]
  );

  useEffect(() => {
    if (pendingRole === undefined) return;
    if (isFetching) {
      hasSeenSwitchFetch.current = true;
    } else if (hasSeenSwitchFetch.current) {
      hasSeenSwitchFetch.current = false;
      setPendingRole(undefined);
    }
  }, [pendingRole, isFetching]);

  const isSwitchingRole = pendingRole !== undefined;

  const setRole = useCallback((next: UserRole) => {
    if (!import.meta.env.DEV) return;
    setDevRoleOverride(next);
  }, []);

  const value = useMemo<AccessContextValue>(
    () => ({
      profile,
      snapshot,
      role,
      userRoles,
      preferredRoleCode,
      activeRoleCode,
      selectRole,
      setRole,
      isLoading,
      isFetching: isFetching || isSwitchingRole,
      isDevRoleOverride,
      can: (permission) => hasPermission(snapshot, permission),
      canAny: (permissions) => hasAnyPermission(snapshot, permissions),
      canAll: (permissions) => hasAllPermissions(snapshot, permissions),
      hasRole: (roleCode) => hasRoleCode(snapshot, roleCode),
      hasAnyRole: (roleCodes) => hasAnyRoleCode(snapshot, roleCodes),
      hasAppRole: (appRole) => hasUserRole(snapshot, appRole),
      hasModule: (moduleCode) => hasModuleAccess(snapshot, moduleCode),
    }),
    [
      profile,
      snapshot,
      role,
      userRoles,
      preferredRoleCode,
      activeRoleCode,
      selectRole,
      setRole,
      isLoading,
      isFetching,
      isSwitchingRole,
      isDevRoleOverride,
    ]
  );

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

export const useAccessControl = (): AccessContextValue => {
  const accessContext = useContext(AccessContext);
  if (!accessContext) {
    throw new Error('useAccessControl must be used within AccessProvider');
  }
  return accessContext;
};

/** @deprecated Use AccessProvider instead. */
export const UserRoleProvider = AccessProvider;
